import { call, put } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import * as crossBorderActions from '../actions/crossBorder';
import * as API from './api';
import { DocumentType } from '../../enums';
import { getDocumentUploadValidationError } from '../../utils/documentUploadHelpers';
import { buildCrossBorderExecutePayload } from '../../utils/crossBorderHelpers';

function* getSpotRate({ payload }) {
  try {
    const spotRate = yield call(API.getSpotRate, payload);
    yield put({
      type: crossBorderActions.GET_SPOT_RATE_SUCCESS,
      payload: spotRate,
    });
  } catch (error) {
    error.name = 'Could not get Spot Rate';
    yield put({ type: crossBorderActions.GET_SPOT_RATE_ERROR, payload: error });
  }
}

function* getCrossBorderQuote({ payload }) {
  try {
    const quotePayload = { ...payload };

    if (payload.transactionDocument && payload.transactionDocument.file) {
      const validationError = getDocumentUploadValidationError(payload.transactionDocument.file);
      if (validationError) {
        throw new Error(validationError);
      }

      const formData = new FormData();
      formData.append('file', payload.transactionDocument.file);
      formData.append('metadata[id]', payload.transactionDocument.id);
      formData.append('metadata[type]', payload.transactionDocument.type);

      const { accessUrl } = yield call(API.uploadDocument, formData, DocumentType.CrossBorder);

      if (!accessUrl) {
        throw new Error('Document upload failed: No URL returned from server. Please contact support.');
      }

      quotePayload.transactionDocument = {
        name: payload.transactionDocument.name,
        id: payload.transactionDocument.id,
        type: payload.transactionDocument.type,
        url: accessUrl,
      };
    }

    const crossBorderQuote = yield call(API.getCrossBorderQuote, quotePayload);
    yield put({
      type: crossBorderActions.GET_CROSS_BORDER_QUOTE_SUCCESS,
      payload: crossBorderQuote,
    });
  } catch (error) {
    const errorPayload = {
      name: 'Could not get Cross Border Quote',
      message: error.message || error.detail || 'Failed to get quote',
      detail: error.detail,
      response: error.response,
      data: error.data,
      ...error,
    };
    yield put({ type: crossBorderActions.GET_CROSS_BORDER_QUOTE_ERROR, payload: errorPayload });
  }
}

function* executeCrossBorderQuote({ payload }) {
  try {
    const requestId = payload?.requestId || uuidv4();

    yield put({
      type: crossBorderActions.TRACK_CROSS_BORDER_QUOTE,
      payload: {
        requestId,
      },
    });

    const executePayload = buildCrossBorderExecutePayload(payload?.quote ?? payload, requestId);
    if (!executePayload) {
      throw new Error('Quote is missing. Please generate a new quote before sending.');
    }

    const result = yield call(API.executeCrossBorderQuote, executePayload);
    yield put({
      type: crossBorderActions.EXECUTE_CROSS_BORDER_QUOTE_SUCCESS,
      payload: result,
    });
  } catch (error) {
    error.name = 'Could not execute Cross Border Quote';
    yield put({ type: crossBorderActions.EXECUTE_CROSS_BORDER_QUOTE_ERROR, payload: error });
  }
}

const sagas = [
  [crossBorderActions.GET_SPOT_RATE, getSpotRate],
  [crossBorderActions.GET_CROSS_BORDER_QUOTE, getCrossBorderQuote],
  [crossBorderActions.EXECUTE_CROSS_BORDER_QUOTE, executeCrossBorderQuote],
];

export default sagas;
